import React from 'react';
import { Building2, Target, Eye, Award, Shield, Leaf } from 'lucide-react';

export const ProfilePage = () => {
  return (
    <section id="profile" className="py-24 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, #FF6B00 1px, transparent 0)',
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Title - Corporate Style */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#FF6B00]/10 backdrop-blur-md rounded-full border border-[#FF6B00]/30 mb-6">
            <div className="w-2 h-2 bg-[#FF6B00] rounded-full animate-pulse"></div>
            <span className="text-[#FF6B00] uppercase tracking-widest text-sm font-bold">About Us</span>
          </div>
          
          <h2 className="text-5xl md:text-6xl font-black text-black mb-6">
            Company <span className="bg-gradient-to-r from-[#FF6B00] to-[#E65F00] bg-clip-text text-transparent">Overview</span>
          </h2>
          
          <p className="text-gray-600 text-xl max-w-4xl mx-auto leading-relaxed">
            A legacy of excellence in turnkey interior execution since 2016
          </p>
        </div>

        {/* Founder's Message - Professional Corporate Layout */}
        <div className="mb-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Professional Founder Image - Left Side */}
            <div className="flex justify-center lg:justify-start order-2 lg:order-1">
              <div className="relative group">
                {/* Professional Photo Frame */}
                <div className="relative w-[380px] h-[480px] lg:w-[420px] lg:h-[520px]">
                  {/* Image Container with Corporate Border */}
                  <div className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl border-4 border-gray-200 bg-gradient-to-br from-gray-100 to-white">
                    <img
                      src="https://customer-assets-cm19k8pv.emergentagent.net/job_lalit-precision/artifacts/bc3ed001c1782500_WhatsApp%20Image%202026-10-29%20at%209.17.17%20PM.jpeg"
                      alt="Pawan Vishwakarma - Founder & CEO, Lalit Interior"
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  
                  {/* Professional Info Card */}
                  <div className="absolute -bottom-8 -right-8 bg-gradient-to-r from-[#FF6B00] to-[#E65F00] px-8 py-6 rounded-2xl shadow-2xl min-w-[280px]">
                    <p className="text-2xl font-black text-white mb-1">
                      Pawan Vishwakarma
                    </p>
                    <p className="text-white/90 text-sm font-semibold uppercase tracking-wider">Founder & CEO</p>
                    <div className="mt-3 pt-3 border-t border-white/30">
                      <p className="text-white/80 text-xs">8+ Years Industry Leadership</p>
                    </div>
                  </div>

                  {/* Corner Accents */}
                  <div className="absolute top-0 left-0 w-20 h-20 border-t-4 border-l-4 border-[#FF6B00] rounded-tl-3xl"></div>
                  <div className="absolute bottom-0 right-0 w-20 h-20 border-b-4 border-r-4 border-[#FF6B00] rounded-br-3xl"></div>
                </div>
              </div>
            </div>

            {/* Professional Message - Right Side */}
            <div className="space-y-8 order-1 lg:order-2">
              <div>
                <p className="text-[#FF6B00] uppercase text-sm tracking-wider font-bold mb-4">Message from the Founder</p>
                <h3 className="text-4xl md:text-5xl font-black text-black leading-tight mb-6">
                  Excellence Through
                  <br />
                  <span className="bg-gradient-to-r from-[#FF6B00] to-[#E65F00] bg-clip-text text-transparent">Innovation & Integrity</span>
                </h3>
              </div>

              <div className="space-y-6 text-gray-700 text-base md:text-lg leading-relaxed">
                <p className="font-medium text-gray-900">
                  At Lalit Interior, we don't just build spaces—we craft experiences that inspire, elevate, and transform the way people live and work.
                </p>
                <p>
                  Since our inception in 2016, we have successfully delivered over <span className="font-bold text-[#FF6B00]">150+ premium projects</span> spanning <span className="font-bold text-[#FF6B00]">2 lakh+ square feet</span> across India's hospitality, commercial, and residential sectors.
                </p>
                <p>
                  Our commitment to excellence is unwavering. We specialize in comprehensive turnkey solutions—from MEP systems and fire safety to complete interior fit-outs—executed with precision, transparency, and uncompromising quality standards.
                </p>
                <p>
                  What sets us apart is our holistic approach: combining cutting-edge technology, sustainable practices, and a client-first philosophy to deliver projects that exceed expectations, every single time.
                </p>
                <div className="mt-8 p-6 bg-gradient-to-r from-[#FF6B00]/10 to-transparent rounded-2xl border-l-4 border-[#FF6B00]">
                  <p className="text-gray-900 font-semibold italic">
                    "We build more than interiors—we build partnerships, trust, and spaces where success thrives."
                  </p>
                  <p className="text-gray-600 text-sm mt-3">— Pawan Vishwakarma, Founder & CEO</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vision & Mission */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow duration-300">
            <div className="flex items-center mb-4">
              <div className="bg-[#FF6B00] bg-opacity-10 p-4 rounded-full mr-4">
                <Eye className="text-[#FF6B00]" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-black">Our Vision</h3>
            </div>
            <p className="text-[#333333] leading-relaxed">
              To be a leading force in the construction and interior industry by delivering innovative, sustainable, and precision-driven turnkey solutions that shape spaces and inspire progress. We envision Lalit Interior as a symbol of quality, trust, and transformative design.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow duration-300">
            <div className="flex items-center mb-4">
              <div className="bg-[#FF6B00] bg-opacity-10 p-4 rounded-full mr-4">
                <Target className="text-[#FF6B00]" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-black">Our Mission</h3>
            </div>
            <p className="text-[#333333] leading-relaxed">
              To deliver excellence in every phase of construction and interior fit-out by combining technical expertise, innovative practices, and uncompromising quality. We are committed to executing projects on time and within budget, while maintaining the highest standards of safety, sustainability, and client satisfaction.
            </p>
          </div>
        </div>

        {/* Core Values */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center text-black mb-8">
            Core <span className="text-[#FF6B00]">Values</span>
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 text-center">
              <div className="bg-[#FF6B00] bg-opacity-10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="text-[#FF6B00]" size={32} />
              </div>
              <h4 className="text-xl font-bold text-black mb-2">Quality</h4>
              <p className="text-[#333333]">Uncompromising standards in every project we deliver</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 text-center">
              <div className="bg-[#FF6B00] bg-opacity-10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="text-[#FF6B00]" size={32} />
              </div>
              <h4 className="text-xl font-bold text-black mb-2">Safety</h4>
              <p className="text-[#333333]">Core priority for workers, clients, and community</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 text-center">
              <div className="bg-[#FF6B00] bg-opacity-10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="text-[#FF6B00]" size={32} />
              </div>
              <h4 className="text-xl font-bold text-black mb-2">Sustainability</h4>
              <p className="text-[#333333]">Environmentally responsible practices in all operations</p>
            </div>
          </div>
        </div>

        {/* Execution Process */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-center text-black mb-8">
            Our Execution <span className="text-[#FF6B00]">Process</span>
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-[#FF6B00] text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                01
              </div>
              <h4 className="text-xl font-bold text-black mb-2">Pre-Construction</h4>
              <p className="text-[#333333]">Planning, design finalization, and resource allocation</p>
            </div>

            <div className="text-center">
              <div className="bg-[#FF6B00] text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                02
              </div>
              <h4 className="text-xl font-bold text-black mb-2">Construction Phase</h4>
              <p className="text-[#333333]">Execution with real-time progress tracking and quality control</p>
            </div>

            <div className="text-center">
              <div className="bg-[#FF6B00] text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                03
              </div>
              <h4 className="text-xl font-bold text-black mb-2">Post-Construction</h4>
              <p className="text-[#333333]">Final inspection, handover, and client support</p>
            </div>
          </div>
        </div>

        {/* Key Highlights */}
        <div className="mt-16">
          <h3 className="text-3xl font-bold text-center text-black mb-8">
            Why Choose <span className="text-[#FF6B00]">Lalit Interior</span>
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start bg-[#F5F5F5] p-6 rounded-xl">
              <div className="bg-[#FF6B00] text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                ✓
              </div>
              <div>
                <h4 className="font-bold text-black mb-1">8+ Years of Proven Experience</h4>
                <p className="text-[#333333] text-sm">Established track record in delivering diverse projects across India</p>
              </div>
            </div>

            <div className="flex items-start bg-[#F5F5F5] p-6 rounded-xl">
              <div className="bg-[#FF6B00] text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                ✓
              </div>
              <div>
                <h4 className="font-bold text-black mb-1">PAN-India Operations</h4>
                <p className="text-[#333333] text-sm">Capacity for large-scale deployments across multiple states</p>
              </div>
            </div>

            <div className="flex items-start bg-[#F5F5F5] p-6 rounded-xl">
              <div className="bg-[#FF6B00] text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                ✓
              </div>
              <div>
                <h4 className="font-bold text-black mb-1">Comprehensive Services</h4>
                <p className="text-[#333333] text-sm">End-to-end solutions from civil work to MEP, HVAC, and fire-fighting</p>
              </div>
            </div>

            <div className="flex items-start bg-[#F5F5F5] p-6 rounded-xl">
              <div className="bg-[#FF6B00] text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                ✓
              </div>
              <div>
                <h4 className="font-bold text-black mb-1">Project Management App</h4>
                <p className="text-[#333333] text-sm">Real-time progress tracking, site reports, and photographic updates</p>
              </div>
            </div>

            <div className="flex items-start bg-[#F5F5F5] p-6 rounded-xl">
              <div className="bg-[#FF6B00] text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                ✓
              </div>
              <div>
                <h4 className="font-bold text-black mb-1">300+ Skilled Manpower</h4>
                <p className="text-[#333333] text-sm">Dedicated team of designers, engineers, and project managers</p>
              </div>
            </div>

            <div className="flex items-start bg-[#F5F5F5] p-6 rounded-xl">
              <div className="bg-[#FF6B00] text-white w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                ✓
              </div>
              <div>
                <h4 className="font-bold text-black mb-1">Transparency & Reliability</h4>
                <p className="text-[#333333] text-sm">Building long-term relationships through trust and quality</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
